// components/entities/pages/organisation-teams/model.ts
import { getUnassignedMembers, readTeamCreditPool } from '#lib/organisation-teams/team-rules';
import type {
  EntityKernelStateRegistry,
  InventoryRegistry,
  KernelHandle,
  KernelViewportBounds,
  LeasingUserDTO,
  OrganisationInventoryEntityState,
  OrganisationRuntimeState,
  OrganisationTeamDTO,
  OrganisationTeamsCanvasRuntimeState,
} from '#types/types';

export const selectOrganisationTeamsRuntime = (
  organisationId: string,
  registry: Record<string, unknown>,
  entityKernelState: EntityKernelStateRegistry,
): OrganisationTeamsCanvasRuntimeState | null => {
  const draft = entityKernelState[`Organisation:${organisationId}`]?.state as { teamsCanvas?: OrganisationTeamsCanvasRuntimeState; runtime?: OrganisationRuntimeState } | undefined;
  const organisation = registry[organisationId] as { teamsCanvas?: OrganisationTeamsCanvasRuntimeState; runtime?: OrganisationRuntimeState } | undefined;
  return draft?.runtime?.teamsCanvas ?? draft?.teamsCanvas ?? organisation?.runtime?.teamsCanvas ?? organisation?.teamsCanvas ?? null;
};

export interface OrganisationPoolNode {
  id: 'organisation';
  label: string;
  total: number;
  used: number;
  reserved: number;
  available: number;
  members: LeasingUserDTO[];
  x: number;
  y: number;
  radius: number;
}

export interface TeamPoolNode {
  team: OrganisationTeamDTO;
  members: LeasingUserDTO[];
  reserved: number;
  used: number;
  remaining: number;
  sourcePlanId: string | null;
  x: number;
  y: number;
  radius: number;
}

export interface TeamsCanvasLayout {
  organisationPool: OrganisationPoolNode;
  teams: TeamPoolNode[];
  bounds: KernelViewportBounds;
  center: { x: number; y: number };
}

export const DEFAULT_TEAMS_VIEWPORT = {
  panOffset: { x: 24, y: 24 },
  zoomLevel: 1,
};

export const UNASSIGNED_TEAM_VALUE = '__unassigned__';

interface MemberAvatarOrbitPosition {
  x: number;
  y: number;
  ringIndex: number;
  angle: number;
}

export interface MemberAvatarPacking {
  avatarSize: number;
  nodeSize: number;
  angularGap: number;
  orbitGap: number;
  extentRadius: number;
  diameter: number;
  shellCount: number;
  positions: MemberAvatarOrbitPosition[];
}

const ORGANISATION_TEAMS_PAGE_SIZE = 200;
const ORGANISATION_TEAMS_MAX_PAGES = 50;
type OrganisationScopedInventoryEntity = 'Team' | 'User';
type OrganisationScopedInventoryFilters = Partial<InventoryRegistry[OrganisationScopedInventoryEntity]['filters']>;

const queryAllOrganisationScopedInventoryPages = async (
  kernel: KernelHandle['kernel'],
  entity: OrganisationScopedInventoryEntity,
  organisationId: string,
  force: boolean,
  ttl?: number,
): Promise<void> => {
  let offset = 0;
  for (let pageIndex = 0; pageIndex < ORGANISATION_TEAMS_MAX_PAGES; pageIndex += 1) {
    if (!kernel.current) return;
    const result = await kernel.current.queryInventory(entity, {
      filters: { organisationId } as OrganisationScopedInventoryFilters,
      limit: ORGANISATION_TEAMS_PAGE_SIZE,
      offset,
      force,
      ...(ttl !== undefined ? { ttl } : {}),
    });
    if (result.error || !result.pagination?.hasMore) return;
    const nextOffset = result.pagination.offset + result.pagination.limit;
    if (nextOffset <= offset) return;
    offset = nextOffset;
  }
};

export const hydrateOrganisationMembersInventory = async (
  kernel: KernelHandle['kernel'],
  organisationId: string,
  options: { force?: boolean; ttl?: number } = {},
): Promise<void> => {
  if (!organisationId) return;
  await queryAllOrganisationScopedInventoryPages(kernel, 'User', organisationId, options.force ?? false, options.ttl);
};

export const hydrateOrganisationTeamsInventory = async (
  kernel: KernelHandle['kernel'],
  organisationId: string,
  options: { force?: boolean; ttl?: number } = {},
): Promise<void> => {
  if (!organisationId) return;
  const force = options.force ?? false;
  await Promise.all([
    queryAllOrganisationScopedInventoryPages(kernel, 'Team', organisationId, force, options.ttl),
    queryAllOrganisationScopedInventoryPages(kernel, 'User', organisationId, force, options.ttl),
  ]);
};

const PADDING = 120;
const CIRCLE_SAFE_AREA_PADDING = 14;
const ORG_MIN_RADIUS = 64;
const TEAM_MIN_RADIUS = 52;
const TEAM_TREE_MAX_COLUMNS = 4;
const TEAM_TREE_HORIZONTAL_GAP = 60;
const TEAM_TREE_VERTICAL_GAP = 92;
const TEAM_TREE_ORG_GAP = 130;
const MEMBER_AVATAR_SIZE = 52;
const MEMBER_AVATAR_CHROME = 4;
const MEMBER_AVATAR_GAP = 4;
const EMPTY_POOL_SAFE_DIAMETER = 76;

export const getMemberDisplayName = (member: LeasingUserDTO): string => {
  const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
  return fullName || member.email;
};

export const sortMembers = (left: LeasingUserDTO, right: LeasingUserDTO): number =>
  getMemberDisplayName(left).toLowerCase().localeCompare(getMemberDisplayName(right).toLowerCase());

const getTeamTreeColumnCount = (teamCount: number): number => {
  const maxColumns = Math.max(1, Math.min(TEAM_TREE_MAX_COLUMNS, teamCount));
  let bestColumns = 1;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let columns = 1; columns <= maxColumns; columns += 1) {
    const rows = Math.ceil(teamCount / columns);
    const lastRowCount = teamCount - columns * Math.max(0, rows - 1) || columns;
    const orphanPenalty = rows > 1 && lastRowCount === 1 ? 10 : 0;
    const score = rows * 3 + Math.abs(columns - rows) * 0.7 + orphanPenalty - columns * 0.01;
    if (score < bestScore) {
      bestScore = score;
      bestColumns = columns;
    }
  }
  return bestColumns;
};

const getRingRadius = (memberCountOnRing: number, minimumCenterDistance: number): number => {
  if (memberCountOnRing <= 1) return 0;
  return minimumCenterDistance / (2 * Math.sin(Math.PI / memberCountOnRing));
};

const distributeMembersAcrossShells = (memberCount: number): number[] => {
  if (memberCount <= 1) return [];
  if (memberCount <= 6) return [memberCount];
  if (memberCount <= 13) return [memberCount - 1];
  if (memberCount <= 24) return [6, memberCount - 7];

  const shellCounts = [6, 12];
  let remaining = memberCount - 19;
  while (remaining > 0) {
    const shellCapacity = Math.min(24, Math.max(6, Math.ceil(remaining / 2)));
    shellCounts.push(Math.min(remaining, shellCapacity));
    remaining -= shellCapacity;
  }
  return shellCounts;
};

export const getMemberAvatarPacking = (memberCount: number): MemberAvatarPacking => {
  const avatarSize = MEMBER_AVATAR_SIZE;
  const nodeSize = avatarSize + MEMBER_AVATAR_CHROME;
  const angularGap = MEMBER_AVATAR_GAP;
  const orbitGap = MEMBER_AVATAR_GAP;
  const minimumCenterDistance = nodeSize + MEMBER_AVATAR_GAP;

  if (memberCount <= 0) {
    return {
      avatarSize,
      nodeSize,
      angularGap,
      orbitGap,
      extentRadius: EMPTY_POOL_SAFE_DIAMETER / 2,
      diameter: EMPTY_POOL_SAFE_DIAMETER,
      shellCount: 0,
      positions: [],
    };
  }

  if (memberCount === 1) {
    const extentRadius = nodeSize / 2;
    return {
      avatarSize,
      nodeSize,
      angularGap,
      orbitGap,
      extentRadius,
      diameter: Math.ceil(extentRadius * 2),
      shellCount: 0,
      positions: [{ x: 0, y: 0, ringIndex: 0, angle: 0 }],
    };
  }

  const positions: MemberAvatarOrbitPosition[] = [];
  let extentRadius = nodeSize / 2;

  const hasCenterAvatar = memberCount > 6;
  if (hasCenterAvatar) {
    positions.push({ x: 0, y: 0, ringIndex: 0, angle: 0 });
  }

  let previousRadius = 0;
  distributeMembersAcrossShells(memberCount).forEach((placedOnShell, shellIndex) => {
    const ringIndex = shellIndex + 1;
    const minimumRingRadius = ringIndex === 1
      ? (hasCenterAvatar ? minimumCenterDistance : 0)
      : previousRadius + minimumCenterDistance;
    const ringRadius = Math.max(getRingRadius(placedOnShell, minimumCenterDistance), minimumRingRadius);
    const angleOffset = ringIndex % 2 === 0 ? Math.PI / Math.max(placedOnShell, 1) : 0;

    for (let index = 0; index < placedOnShell; index += 1) {
      const angle = -Math.PI / 2 + angleOffset + (Math.PI * 2 * index) / placedOnShell;
      positions.push({
        x: Math.cos(angle) * ringRadius,
        y: Math.sin(angle) * ringRadius,
        ringIndex,
        angle,
      });
    }

    extentRadius = Math.max(extentRadius, ringRadius + nodeSize / 2);
    previousRadius = ringRadius;
  });

  return {
    avatarSize,
    nodeSize,
    angularGap,
    orbitGap,
    extentRadius,
    diameter: Math.ceil(extentRadius * 2),
    shellCount: Math.max(0, ...positions.map((position) => position.ringIndex)),
    positions,
  };
};

const getRadiusForPacking = (minRadius: number, packing: MemberAvatarPacking): number =>
  Math.max(minRadius, Math.ceil(packing.extentRadius + CIRCLE_SAFE_AREA_PADDING));

const getOrganisationPoolRadius = (memberCount: number): number =>
  getRadiusForPacking(ORG_MIN_RADIUS, getMemberAvatarPacking(memberCount));

const getTeamPoolRadius = (memberCount: number): number =>
  getRadiusForPacking(TEAM_MIN_RADIUS, getMemberAvatarPacking(memberCount));

export const buildTeamsCanvasLayout = (
  organisation: OrganisationInventoryEntityState | null,
  members: LeasingUserDTO[],
  teams: OrganisationTeamDTO[],
): TeamsCanvasLayout => {
  const plans = organisation?.plans ?? [];
  const membersById = new Map(members.map((member) => [member.id, member]));
  const unassignedMembers = getUnassignedMembers(members, teams).sort(sortMembers);

  const total = plans.reduce((sum, plan) => sum + (plan.includedCredits ?? 0), 0);
  const used = plans.reduce((sum, plan) => sum + (plan.usedCredits ?? 0), 0);
  const reserved = plans.reduce((sum, plan) => sum + (plan.reservedCredits ?? 0), 0);
  const available = Math.max(total - used - reserved, 0);

  const orgRadius = getOrganisationPoolRadius(unassignedMembers.length);
  const teamMemberGroups = teams.map((team) => ({
    team,
    radius: getTeamPoolRadius(team.memberIds.length),
    members: team.memberIds
      .map((memberId) => membersById.get(memberId))
      .filter((member): member is LeasingUserDTO => Boolean(member))
      .sort(sortMembers),
  }));
  const columns = getTeamTreeColumnCount(Math.max(teamMemberGroups.length, 1));
  const rows = Array.from({ length: Math.ceil(teamMemberGroups.length / columns) }, (_, rowIndex) =>
    teamMemberGroups.slice(rowIndex * columns, rowIndex * columns + columns),
  );

  let rowTop = orgRadius * 2 + TEAM_TREE_ORG_GAP;
  const teamNodes: TeamPoolNode[] = [];
  rows.forEach((row) => {
    const rowHeight = Math.max(...row.map((entry) => entry.radius * 2));
    const rowWidth = row.reduce((sum, entry) => sum + entry.radius * 2, 0) + Math.max(0, row.length - 1) * TEAM_TREE_HORIZONTAL_GAP;
    let cursorX = -rowWidth / 2;
    row.forEach(({ team, members: teamMembers, radius }) => {
      const pool = readTeamCreditPool(team);
      teamNodes.push({
        team,
        members: teamMembers,
        reserved: pool.reserved,
        used: pool.used,
        remaining: pool.remaining,
        sourcePlanId: pool.sourcePlanId,
        x: cursorX,
        y: rowTop,
        radius,
      });
      cursorX += radius * 2 + TEAM_TREE_HORIZONTAL_GAP;
    });
    rowTop += rowHeight + TEAM_TREE_VERTICAL_GAP;
  });

  const minX = Math.min(-orgRadius, ...teamNodes.map((node) => node.x));
  const maxX = Math.max(orgRadius, ...teamNodes.map((node) => node.x + node.radius * 2));
  const maxY = Math.max(orgRadius * 2, ...teamNodes.map((node) => node.y + node.radius * 2));
  const shiftX = PADDING - minX;
  const shiftY = PADDING;
  const shiftedTeams = teamNodes.map((node) => ({ ...node, x: node.x + shiftX, y: node.y + shiftY }));
  const orgCenter = { x: shiftX, y: shiftY + orgRadius };

  return {
    organisationPool: {
      id: 'organisation',
      label: organisation?.name || 'Organisation',
      total,
      used,
      reserved,
      available,
      members: unassignedMembers,
      x: orgCenter.x - orgRadius,
      y: orgCenter.y - orgRadius,
      radius: orgRadius,
    },
    teams: shiftedTeams,
    center: orgCenter,
    bounds: {
      x: 0,
      y: 0,
      w: Math.max(900, maxX + shiftX + PADDING),
      h: Math.max(700, maxY + shiftY + PADDING),
    },
  };
};

export const poolConnectorPath = (organisationNode: OrganisationPoolNode, teamNode: TeamPoolNode): string => {
  const fromX = organisationNode.x + organisationNode.radius;
  const fromY = organisationNode.y + organisationNode.radius * 2;
  const toX = teamNode.x + teamNode.radius;
  const toY = teamNode.y;
  const midY = fromY + Math.max(74, (toY - fromY) / 2);
  return `M ${fromX} ${fromY} C ${fromX} ${midY}, ${toX} ${midY}, ${toX} ${toY}`;
};
